// Arc flash label PDFs, drawn in the app. A port of af_label.py, line for line.
// The layout is locked (AF_Label_System.md, approved 10/14/2026). A layout change goes to af_label.py
// first, then here. The pixel test (tests/pixel) renders the same rows through both at 300 dpi and fails
// the build on any difference in the print files.
//
// 5 x 3 in landscape. The top 0.75 in is left blank for the pre-printed WARNING or DANGER header.
// Helvetica is the PDF standard font, not embedded, in both, so the two draw the same glyphs.

import { degrees, PDFDocument, rgb, StandardFonts, type PDFFont, type PDFPage } from "pdf-lib";
import { f1, g } from "./py-format";
import { shiftPage, START_OFFSET, type PrinterOffset } from "./printer-offset";
export { f1, g };

const inch = 72;
const W = 5 * inch, H = 3 * inch;
export const HEADER_H = 0.75 * inch; // pre-printed WARNING/DANGER band, keep clear. Confirm by measurement
const BORDER = 0.12 * inch; // side and bottom margin (stock has no printed border)
export const X_NUDGE = 0, Y_NUDGE = 0; // kept at 0 to match af_label.py. The admin's offset moves the whole page (printer-offset.ts)
export const ROTATE_180 = false; // flip content if the stock ever feeds the other way
const REVIEW_YEARS = 5; // assessment dates older than this are flagged (70E review interval)
const OVERDUTY_TITLE = "Overduty Device: Interrupting Rating Exceeded";
const black = rgb(0, 0, 0), white = rgb(1, 1, 1);

// ---- Config: the Config tab of AF_Label_Data.xlsx, the standard ----

export interface LabelConfig {
  ieCeilingCal: number;
  gloveProtectorText: string;
  groups: { text: string; above: number; upto: number; range: string; provided: boolean }[];
  /** Up to volts, glove text. Sorted. */
  gloves: [number, string][];
}

/** The workbook's Config tab as it stands (read 2026-10-21). */
export const DEFAULT_CONFIG: LabelConfig = {
  ieCeilingCal: 12,
  gloveProtectorText: "with Leather Protectors",
  groups: [
    { text: "PPE Group 1", above: 0, upto: 12, range: "1.2 to 12", provided: true },
    { text: "PPE Group 2: Not Provided", above: 12, upto: 40, range: "12 to 40", provided: false },
    { text: "No PPE Rated", above: 40, upto: 9999, range: "Above 40", provided: false },
  ],
  gloves: [
    [1000, "Class 0 Rubber Gloves"],
    [7500, "Class 1 Rubber Gloves"],
    [17000, "Class 2 Rubber Gloves"],
    [26500, "Class 3 Rubber Gloves"],
    [36000, "Class 4 Rubber Gloves"],
  ],
};

/** Reads the Config tab (rows of cells), the way af_label.py read_config does. */
export function configOf(rows: readonly (readonly unknown[])[]): LabelConfig {
  const c: LabelConfig = { ieCeilingCal: 12, gloveProtectorText: "with Leather Protectors", groups: [], gloves: [] };
  let mode = "";
  for (const r of rows) {
    const a = r[0];
    if (a === "Setting") { mode = "set"; continue; }
    if (a === "Group_Text") { mode = "grp"; continue; }
    if (a === "Up_To_Voltage_V") { mode = "glv"; continue; }
    if (a === null || a === undefined || a === "" || a === "PPE Groups" || a === "Glove Class by Voltage") continue;
    const b = r[1];
    if (mode === "set" && b !== null && b !== undefined && b !== "") {
      if (a === "IE_Ceiling_cal") c.ieCeilingCal = Number(b);
      if (a === "Glove_Protector_Text") c.gloveProtectorText = String(b);
    } else if (mode === "grp" && b !== "" && b != null && r[2] !== "" && r[2] != null) {
      c.groups.push({ text: String(a), above: Number(b), upto: Number(r[2]), range: String(r[3]), provided: String(r[4]).toUpperCase() === "Y" });
    } else if (mode === "glv" && typeof a === "number") c.gloves.push([a, String(b)]);
  }
  c.gloves.sort((x, y) => x[0] - y[0]);
  return c;
}

// ---- One row to label values: a port of to_label ----

/** One Label_Data row. Field names are the workbook's headers. */
export type LabelRecord = Record<string, unknown>;

export interface LabelValues {
  title?: string;
  voltage: string;
  lab: string;
  rab: string;
  ie: string;
  wd: string;
  afb: string;
  afc: string;
  gloves: string;
  gloves2: string;
  equip_id: string;
  date: string;
  iea_by: string;
  aic?: string;
  isolate_at?: string;
  ppe_group: string;
  ppe_range: string;
}

export type LabelKind = "STANDARD" | "ABOVE CEILING" | "OVERDUTY";

const numOf = (v: unknown): number | null | "BAD" => {
  if (v === null || v === undefined || (typeof v === "string" && !v.trim())) return null;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  return Number.isFinite(n) ? n : "BAD";
};
/** A date as the label prints it, MM/DD/YYYY, from a Date, an Excel day number or MM/DD/YYYY text. */
function dateOf(v: unknown): { text: string; date: Date | null; isText: boolean } {
  if (v instanceof Date) return { text: mdY(v), date: v, isText: false };
  if (typeof v === "number" && v > 20000 && v < 80000) {
    const d = new Date(Math.round((v - 25569) * 86400000));
    return { text: mdY(d, true), date: d, isText: false };
  }
  const s = typeof v === "string" ? v.trim() : "";
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (m) return { text: s, date: new Date(Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2]))), isText: false };
  return { text: s, date: null, isText: !!s };
}
const mdY = (d: Date, utc = false) =>
  `${String((utc ? d.getUTCMonth() : d.getMonth()) + 1).padStart(2, "0")}/${String(utc ? d.getUTCDate() : d.getDate()).padStart(2, "0")}/${utc ? d.getUTCFullYear() : d.getFullYear()}`;

export function toLabel(rec: LabelRecord, cfg: LabelConfig = DEFAULT_CONFIG, today = new Date()): { d: LabelValues | null; kind: LabelKind; skip: string[]; warn: string[] } {
  const skip: string[] = [], warn: string[] = [];
  const keys = ["Nominal_Voltage_V", "LAB_in", "RAB_in", "Incident_Energy_cal", "Working_Distance_in", "Arc_Flash_Boundary_in", "Available_Fault_Current_kA", "Interrupting_Rating_kA"] as const;
  const nums = Object.fromEntries(keys.map((k) => [k, numOf(rec[k])])) as Record<(typeof keys)[number], number | null | "BAD">;
  for (const k of keys) if (nums[k] === "BAD") skip.push(`${k} is not a number`);
  for (const k of ["Nominal_Voltage_V", "LAB_in", "RAB_in", "Available_Fault_Current_kA"] as const) if (nums[k] === null) skip.push(`${k} missing`);
  for (const k of ["Equipment_ID", "Assessment_Date", "IEA_By"]) if (rec[k] === null || rec[k] === undefined || rec[k] === "") skip.push(`${k} missing`);

  const ir = nums.Interrupting_Rating_kA;
  const overduty = ir !== null && ir !== "BAD";
  let kind: LabelKind;
  if (overduty) {
    kind = "OVERDUTY";
    if (!rec.Isolate_At) skip.push("Isolate_At missing (required for overduty)");
    const afc = nums.Available_Fault_Current_kA;
    if (typeof afc === "number" && (ir as number) >= afc) warn.push("Interrupting rating is not below available fault current. Confirm the device is overduty.");
  } else {
    for (const k of ["Incident_Energy_cal", "Working_Distance_in", "Arc_Flash_Boundary_in"] as const) if (nums[k] === null) skip.push(`${k} missing`);
    const ie = nums.Incident_Energy_cal;
    kind = typeof ie === "number" && ie > cfg.ieCeilingCal ? "ABOVE CEILING" : "STANDARD";
  }

  const dt = dateOf(rec.Assessment_Date);
  if (dt.date) {
    const age = (today.getTime() - dt.date.getTime()) / 86400000 / 365.25;
    if (age > REVIEW_YEARS) warn.push(`Assessment date is ${age.toFixed(1)} years old. Review per 70E 130.5.`);
  } else if (dt.isText) warn.push("Assessment_Date is text, not a date. Printed as entered.");
  if (skip.length) return { d: null, kind, skip, warn };

  const v = nums.Nominal_Voltage_V as number;
  const vtype = String(rec.Voltage_Type ?? "AC").trim().toUpperCase() || "AC";
  const glove = cfg.gloves.find(([upto]) => v <= upto)?.[1];
  if (!glove) {
    skip.push(`No glove class in Config for ${g(v)} V`);
    return { d: null, kind, skip, warn };
  }
  const base = {
    voltage: `${g(v)} V${vtype}`,
    lab: `${g(nums.LAB_in as number)} in`,
    rab: `${g(nums.RAB_in as number)} in`,
    afc: `${f1(nums.Available_Fault_Current_kA as number)} kA`,
    gloves: glove,
    gloves2: cfg.gloveProtectorText,
    equip_id: String(rec.Equipment_ID).trim(),
    date: dt.text,
    iea_by: String(rec.IEA_By).trim(),
  };
  if (overduty) {
    return { d: { ...base, title: OVERDUTY_TITLE, ie: "OVERDUTY", wd: "N/A", afb: "N/A", aic: `${g(ir as number)} kAIC`, isolate_at: String(rec.Isolate_At).trim(), ppe_group: "", ppe_range: "" }, kind, skip, warn };
  }
  const ie = nums.Incident_Energy_cal as number;
  const grp = cfg.groups.find((x) => x.above < ie && ie <= x.upto);
  if (!grp) {
    skip.push(`No PPE group in Config covers ${ie} cal/cm2`);
    return { d: null, kind, skip, warn };
  }
  if (kind === "STANDARD" && !grp.provided) warn.push("PPE group is marked not provided but IE is under the ceiling. Check Config.");
  return { d: { ...base, ie: f1(ie), wd: `${g(nums.Working_Distance_in as number)} in`, afb: `${g(nums.Arc_Flash_Boundary_in as number)} in`, ppe_group: grp.text, ppe_range: `${grp.range} cal/cm` }, kind, skip, warn };
}

// ---- Drawing: a port of content() and header() ----

interface Fonts {
  reg: PDFFont;
  bold: PDFFont;
}

class Pen {
  constructor(private page: PDFPage, private f: Fonts) {}
  private fill = black;
  /** Glyph widths added up, no kerning, the way reportlab's stringWidth measures. pdf-lib's own
   *  widthOfTextAtSize adds kerning pairs but draws without them, which shifts every centred line. */
  width(t: string, bold: boolean, size: number) {
    const font = bold ? this.f.bold : this.f.reg;
    let w = 0;
    for (const ch of t) w += font.widthOfTextAtSize(ch, 1000);
    return (w * size) / 1000;
  }
  setFill(c: ReturnType<typeof rgb>) {
    this.fill = c;
  }
  text(x: number, y: number, t: string, bold: boolean, size: number) {
    this.page.drawText(t, { x: x + X_NUDGE, y: y + Y_NUDGE, size, font: bold ? this.f.bold : this.f.reg, color: this.fill });
  }
  centred(x: number, y: number, t: string, bold: boolean, size: number) {
    this.text(x - this.width(t, bold, size) / 2, y, t, bold, size);
  }
  right(x: number, y: number, t: string, bold: boolean, size: number) {
    this.text(x - this.width(t, bold, size), y, t, bold, size);
  }
  line(x1: number, y1: number, x2: number, y2: number) {
    this.page.drawLine({ start: { x: x1 + X_NUDGE, y: y1 + Y_NUDGE }, end: { x: x2 + X_NUDGE, y: y2 + Y_NUDGE }, thickness: 1.2, color: black });
  }
  rect(x: number, y: number, w: number, h: number) {
    this.page.drawRectangle({ x: x + X_NUDGE, y: y + Y_NUDGE, width: w, height: h, color: this.fill, borderWidth: 0 });
  }
}

/** Text ending in cal/cm with a raised 2. */
function cal(p: Pen, x: number, y: number, text: string, size: number, align: "right" | "center") {
  const w = p.width(text, true, size) + p.width("2", true, size * 0.6);
  const x0 = align === "right" ? x - w : x - w / 2;
  p.text(x0, y, text, true, size);
  p.text(x0 + p.width(text, true, size), y + size * 0.4, "2", true, size * 0.6);
}

function shrink(p: Pen, t: string, bold: boolean, start: number, max: number, min: number, step: number) {
  let s = start;
  while (p.width(t, bold, s) > max && s > min) s -= step;
  return s;
}

function content(p: Pen, d: LabelValues, cfg: LabelConfig) {
  const L = BORDER + 0.08 * inch;
  const R = W - BORDER - 0.08 * inch;
  const top = H - HEADER_H - 0.14 * inch;
  p.setFill(black);

  const title = d.title ?? "Arc Flash and Shock Hazard Present";
  let ts = 14;
  while (p.width(title, true, ts) > R - L - 4 && ts > 10) ts -= 0.25;
  p.centred(W / 2, top - 17, title, true, ts);
  p.centred(W / 2, top - 31, `Nominal System Voltage:  ${d.voltage}`, true, 11);
  const y1 = top - 37;
  p.line(L, y1, R, y1);

  const y2 = 0.56 * inch;
  const mid = L + 2.5 * inch;
  const rows: [string, string][] = [
    ["Limited Approach Boundary:", d.lab],
    ["Restricted Approach Boundary:", d.rab],
    ["Incident Energy:", d.aic ? d.ie : `${d.ie} cal/cm`],
    ["Working Distance:", d.wd],
    ["Arc Flash Boundary:", d.afb],
  ];
  const step = (y1 - y2) / rows.length;
  rows.forEach(([lab, val], i) => {
    const yy = y1 - step * (i + 0.72);
    p.text(L + 2, yy, lab, false, 9.5);
    if (val.endsWith("cal/cm")) cal(p, mid - 6, yy, val, 10, "right");
    else p.right(mid - 6, yy, val, true, 10);
  });
  p.line(mid, y1, mid, y2);

  const bx = mid + 6, bw = R - mid - 6;
  const cx = bx + bw / 2;
  p.setFill(black);
  p.rect(bx, y1 - 16, bw, 13);
  p.setFill(white);
  p.centred(cx, y1 - 12.5, "PPE REQUIRED", true, 10);
  p.setFill(black);
  const overduty = !!d.aic;
  const over = !overduty && parseFloat(d.ie) > cfg.ieCeilingCal;
  const gl = `${d.gloves.replace(" Rubber", "")} ${d.gloves2.replace("with Leather Protectors", "w/ Protectors")}`;
  let gs = 9;
  while (p.width(gl, false, gs) > bw - 6 && gs > 6.5) gs -= 0.25;
  p.centred(cx, y1 - 27, gl, false, gs);
  let rs = 15;
  while (p.width(`${d.ppe_range}2`, true, rs) > bw - 6 && rs > 10) rs -= 0.5;
  if (overduty) {
    const nt = "NO ARC FLASH PPE RATED";
    p.centred(cx, y1 - 40, nt, true, shrink(p, nt, true, 11, bw - 6, 7, 0.25));
    p.centred(cx, y1 - 49, "Energized work prohibited", false, 8.5);
    const bt = y1 - 52, bb = y2 + 2;
    p.rect(bx, bb, bw, bt - bb);
    p.setFill(white);
    const msg = "UPSTREAM ISOLATION REQUIRED";
    p.centred(cx, bt - 8.5, msg, true, shrink(p, msg, true, 9, bw - 6, 6.5, 0.25));
    const iso = `ISOLATE AT: ${d.isolate_at}`;
    p.centred(cx, bb + 3, iso, true, shrink(p, iso, true, 7.5, bw - 6, 6, 0.25));
    p.setFill(black);
  } else if (over) {
    cal(p, cx, y1 - 44, d.ppe_range, rs, "center");
    p.centred(cx, y1 - 56, d.ppe_group, true, 9.5);
    const bt = y1 - 60, bb = y2 + 2;
    p.rect(bx, bb, bw, bt - bb);
    const msg = "UPSTREAM ISOLATION REQUIRED";
    const ms = shrink(p, msg, true, 9, bw - 6, 6.5, 0.25);
    p.setFill(white);
    p.centred(cx, bb + (bt - bb) / 2 - ms * 0.35, msg, true, ms);
    p.setFill(black);
  } else {
    cal(p, cx, y1 - 47, d.ppe_range, rs, "center");
    p.centred(cx, y1 - 60, d.ppe_group, false, 9.5);
  }

  p.line(L, y2, R, y2);
  const FS = 8.5;
  const f1y = y2 - 13, f2y = y2 - 25;
  const left: [number, string, string][] = d.aic
    ? [[f1y, "Equipment ID:", d.equip_id], [f2y, "Assessment Date:", `${d.date} (${d.iea_by})`]]
    : [[f1y, "Equipment ID:", d.equip_id], [f2y, "Assessment Date:", d.date]];
  const right: [number, string, string][] = d.aic
    ? [[f1y, "Available Fault Current:", d.afc], [f2y, "Interrupting Rating:", d.aic]]
    : [[f1y, "Available Fault Current:", d.afc], [f2y, "IEA By:", d.iea_by]];
  for (const [fy, lab, val] of left) {
    p.text(L + 2, fy, lab, false, FS);
    p.text(L + 2 + p.width(lab, false, FS) + 4, fy, val, true, FS);
  }
  for (const [fy, lab, val] of right) {
    const vw = p.width(val, true, FS);
    p.right(R - 2 - vw - 4, fy, lab, false, FS);
    p.right(R - 2, fy, val, true, FS);
  }
}

/** Simulated pre-printed stock header, for review copies only. Never sent to the printer. */
function header(page: PDFPage, p: Pen, signal: "WARNING" | "DANGER") {
  const band = signal === "WARNING" ? rgb(0xf7 / 255, 0x94 / 255, 0x1d / 255) : rgb(0xc8 / 255, 0x10 / 255, 0x2e / 255);
  const word = signal === "WARNING" ? black : white;
  const r = 0.08 * inch;
  const hb = H - HEADER_H;
  // Outline: a rounded rectangle, white, grey 0.75 pt edge. SVG paths here run top-down from (0, H).
  const k = 0.5523 * r;
  const x0 = 0.5, y0 = 0.5, x1 = W - 0.5, y1 = H - 0.5;
  const sy = (y: number) => H - y;
  const round = `M${x0 + r} ${sy(y0)} H${x1 - r} C${x1 - r + k} ${sy(y0)} ${x1} ${sy(y0 + r - k)} ${x1} ${sy(y0 + r)} V${sy(y1 - r)} C${x1} ${sy(y1 - r + k)} ${x1 - r + k} ${sy(y1)} ${x1 - r} ${sy(y1)} H${x0 + r} C${x0 + r - k} ${sy(y1)} ${x0} ${sy(y1 - r + k)} ${x0} ${sy(y1 - r)} V${sy(y0 + r)} C${x0} ${sy(y0 + r - k)} ${x0 + r - k} ${sy(y0)} ${x0 + r} ${sy(y0)} Z`;
  page.drawSvgPath(round, { x: 0, y: H, color: white, borderColor: rgb(0xbb / 255, 0xbb / 255, 0xbb / 255), borderWidth: 0.75 });
  const bandPath = `M${x0} ${sy(hb)} V${sy(y1 - r)} C${x0} ${sy(y1 - r + k)} ${x0 + r - k} ${sy(y1)} ${x0 + r} ${sy(y1)} H${x1 - r} C${x1 - r + k} ${sy(y1)} ${x1} ${sy(y1 - r + k)} ${x1} ${sy(y1 - r)} V${sy(hb)} Z`;
  page.drawSvgPath(bandPath, { x: 0, y: H, color: band, borderWidth: 0 });
  page.drawRectangle({ x: 0.5, y: hb - 2.5, width: W - 1, height: 2.5, color: black, borderWidth: 0 });
  const fs = 36;
  const tw = p.width(signal, true, fs);
  const tri = 34;
  const gx = (W - (tri + 8 + tw)) / 2;
  const ty = hb + (HEADER_H - fs * 0.72) / 2;
  page.drawSvgPath(`M${gx} ${sy(ty)} L${gx + tri} ${sy(ty)} L${gx + tri / 2} ${sy(ty + tri * 0.9)} Z`, { x: 0, y: H, color: word, borderWidth: 0 });
  p.setFill(band);
  p.centred(gx + tri / 2 - X_NUDGE, ty + 3 - Y_NUDGE, "!", true, 20);
  p.setFill(word);
  p.text(gx + tri + 8 - X_NUDGE, ty - Y_NUDGE, signal, true, fs);
  p.setFill(black);
}

async function newDoc(title: string) {
  const doc = await PDFDocument.create();
  doc.setTitle(title);
  doc.setProducer("SafeTag");
  doc.setCreator("SafeTag Arc Flash Labels");
  const fonts: Fonts = { reg: await doc.embedFont(StandardFonts.Helvetica), bold: await doc.embedFont(StandardFonts.HelveticaBold) };
  return { doc, fonts };
}

function drawPage(doc: PDFDocument, fonts: Fonts, d: LabelValues, cfg: LabelConfig, preview?: "WARNING" | "DANGER", offset?: PrinterOffset) {
  const page = doc.addPage([W, H]);
  if (ROTATE_180 && !preview) page.setRotation(degrees(180));
  const p = new Pen(page, fonts);
  if (preview) header(page, p, preview);
  content(p, d, cfg);
  // The print files move with the arc flash roll offset. The preview is for reading, never printed, so it stays put.
  if (offset && !preview) shiftPage(page, offset, ROTATE_180);
}

export interface LabelSet {
  /** Content only, for the WARNING roll. Null when no WARNING label prints. */
  warning: Uint8Array | null;
  /** Content only, for the DANGER roll. Overduty labels only. */
  danger: Uint8Array | null;
  /** Every label with a simulated header. Review and sharing only. Never printed. */
  preview: Uint8Array | null;
  /** Workbook_Row, Equipment_ID, Label_Type, Result, Reason, the same columns af_label.py writes. */
  exceptionsCsv: string;
  counts: { warning: number; danger: number; skipped: number; flagged: number };
}

/** build_all: rows with Print = Y become the print files, the preview and the exceptions. */
export async function buildLabelSet(records: readonly { row: number; rec: LabelRecord }[], cfg: LabelConfig = DEFAULT_CONFIG, today = new Date(), offset: PrinterOffset = START_OFFSET.arcFlashRoll): Promise<LabelSet> {
  const pages: Record<"WARNING" | "DANGER", LabelValues[]> = { WARNING: [], DANGER: [] };
  const report: (string | number)[][] = [];
  for (const { row, rec } of records) {
    if (String(rec.Print ?? "").trim().toUpperCase() !== "Y") continue;
    const { d, kind, skip, warn } = toLabel(rec, cfg, today);
    const eid = String(rec.Equipment_ID ?? "");
    if (!d) {
      report.push([row, eid, kind, "SKIPPED", skip.join("; ")]);
      continue;
    }
    pages[kind === "OVERDUTY" ? "DANGER" : "WARNING"].push(d);
    if (warn.length) report.push([row, eid, kind, "PRINTED WITH WARNING", warn.join("; ")]);
  }
  const out: Partial<Record<"WARNING" | "DANGER", Uint8Array>> = {};
  for (const stock of ["WARNING", "DANGER"] as const) {
    if (!pages[stock].length) continue;
    const { doc, fonts } = await newDoc(`Alltech Arc Flash Labels (${stock} stock)`);
    for (const d of pages[stock]) drawPage(doc, fonts, d, cfg, undefined, offset);
    out[stock] = await doc.save();
  }
  let preview: Uint8Array | null = null;
  if (pages.WARNING.length + pages.DANGER.length) {
    const { doc, fonts } = await newDoc("Alltech Arc Flash Labels (preview, not for printing)");
    for (const stock of ["WARNING", "DANGER"] as const) for (const d of pages[stock]) drawPage(doc, fonts, d, cfg, stock);
    preview = await doc.save();
  }
  const csvCell = (v: string | number) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  const exceptionsCsv = [["Workbook_Row", "Equipment_ID", "Label_Type", "Result", "Reason"], ...report].map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
  return {
    warning: out.WARNING ?? null,
    danger: out.DANGER ?? null,
    preview,
    exceptionsCsv,
    counts: { warning: pages.WARNING.length, danger: pages.DANGER.length, skipped: report.filter((r) => r[3] === "SKIPPED").length, flagged: report.filter((r) => r[3] !== "SKIPPED").length },
  };
}
